import React, { Fragment } from 'react'
import Axios from 'axios'
import { Modal } from '@pan/cloud-base'
import { get, noop } from 'lodash'

class NetworkError extends Error {
  constructor(msg, code = 0) {
    super(msg)
    this.code = code
  }
}

export const getLoginUrl = (location = window.location) => {
  let param = ''
  if (location.pathname && location.pathname !== '/' && location.pathname.startsWith('/')) {
    param = `?RelayState=${encodeURIComponent(`${location.pathname}${location.search || ''}`)}`
  }
  return `/saml/login${param}`
}

export const redirectToLogin = (location) => {
  window.location.href = getLoginUrl(location)
}

export const showLoginDialog = (cfg = {}) => {
  if (showLoginDialog.modal) {
    return showLoginDialog.modal
  }
  let countDownEl
  let countDown = 15
  let interval = setInterval(() => {
    if (!countDownEl) {
      return // should not happen
    }
    countDownEl.textContent = `${--countDown}`
    if (countDown <= 0) {
      interval = clearInterval(interval)
      redirectToLogin()
    }
  }, 1000)
  const content = <Fragment>
    <p>{cfg.content || 'You browser session has expired, or you have not signed in yet. Please sign in first.'}</p>
    <p>You will be redirected to the sign in page in <strong ref={el => countDownEl = el}>{countDown}</strong> seconds.</p>
  </Fragment>
  const modal = Modal.error({
    title: 'Not Authenticated',
    width: 500,
    promisify: true,
    maskClosable: false,
    className: 'pan-modal',
    okText: 'Sign in',
    ...cfg,
    content,
    onOk: () => {
      interval = clearInterval(interval)
      if (countDownEl) {
        countDownEl.textContent = '0'
      }
      redirectToLogin()
      return new Promise(noop) // wait forever
    }
  })
  const { destroy } = modal
  modal.destroy = () => {
    interval = clearInterval(interval)
    showLoginDialog.modal = undefined
    return destroy()
  }
  showLoginDialog.modal = modal
  const done = () => {
    showLoginDialog.modal = undefined
  }
  modal.then(done, done)
  return modal
}

export const setupAxiosInterceptors = (axios = Axios, options = {}) => {
  const ref = axios.interceptors.globalErrorInterceptor || {}
  if (axios.interceptors.globalErrorInterceptor) { // ensure only inject once per axios instance
    return ref
  }
  const ignoreFn = options.ignore && options.ignore.test ? (url) => options.ignore.test(url) :
    (typeof options.ignore === 'function') ? options.ignore : () => false

  const checkIsAuthenticated = () => {
    if (checkIsAuthenticated.promise) {
      return checkIsAuthenticated.promise
    }
    checkIsAuthenticated.promise = axios.get('/saml/credentials', { skipInterceptors: true }).then(resp => {
      if (get(resp, 'data.isAuthenticated') !== true) {
        throw new NetworkError('Not Authenticated', 401)
      }
      checkIsAuthenticated.promise = undefined
      throw new NetworkError('No Permission', 403)
    }, () => {
      checkIsAuthenticated.promise = undefined
      throw new NetworkError('Network Error')
    })
    return checkIsAuthenticated.promise
  }

  const showErrorDialog = (error) => {
    if (error.code === 401) {
      const done = () => {
        ref.dialog = undefined
        throw error
      }
      if (!ref.dialog) {
        ref.dialog = showLoginDialog()
      }
      return ref.dialog.then(done, done)
    }
    return Promise.reject(error)
  }

  ref.response = axios.interceptors.response.use(resp => resp, (error) => {
    // flag to skip to prevent recursive calls
    if (error.config && error.config.skipInterceptors !== true && !ignoreFn(error.config.url, error.config)) {
      // console.error('axios.interceptors.response error', error, JSON.stringify(error, null, 2))
      const status = get(error, 'response.status')
      if (status === 401 || status === 403 ||
        (!status && !error.response && error.request && !error.request.responseURL)) { // connection error
        return checkIsAuthenticated().catch((err) => showErrorDialog(err))
      }
    }
    return Promise.reject(error)
  })

  ref.destroy = () => {
    axios.interceptors.response.eject(ref.response)
    if (ref.dialog && ref.dialog.destroy) {
      ref.dialog.destroy()
      ref.dialog = undefined
    }
    axios.interceptors.globalErrorInterceptor = undefined
  }
  axios.interceptors.globalErrorInterceptor = ref
  return ref
}

setupAxiosInterceptors.showLoginDialog = showLoginDialog

export default setupAxiosInterceptors
